/**
 * Checkout custom fields (locations.custom_fields, JSONB).
 *
 * Merchants define up to two fields per location that customers fill in at
 * checkout; the booking frontend (ClickAndShare-frontend-v3) renders them.
 * This module is the admin side of that contract:
 *
 *   - the field shape and the frontend's defaults (200 characters for `text`,
 *     1000 characters / 4 rows for `textarea`);
 *   - the HTML allowlist for labels, identical to the frontend's
 *     src/utils/sanitize.js, so a label previews here exactly as it renders
 *     there;
 *   - the conversions between the stored label (HTML with <br>) and the
 *     multi-line editor (newlines), and between HTML and the plain text an
 *     input placeholder can show.
 */

import DOMPurify from 'dompurify';

export type CustomFieldType = 'text' | 'textarea' | 'date' | 'datetime' | 'info';

export interface CustomFieldConfig {
  key: string;
  /** May contain the HTML allowed by {@link sanitizeCustomFieldHtml}. */
  label: string;
  type: CustomFieldType;
  required: boolean;
  maxLength?: number;
  /** Visible rows of a `textarea` field. */
  rows?: number;
  /** Grey hint inside an empty `text` or `textarea` field. Plain text, `\n` for line breaks. */
  placeholder?: string;
}

/** One entry of location_translations.custom_fields; matched to the base field by `key`. */
export interface CustomFieldTranslation {
  key: string;
  label?: string;
  placeholder?: string;
}

export const CUSTOM_FIELD_DEFAULTS = {
  text: { maxLength: 200, maxLengthLimit: 500 },
  textarea: { maxLength: 1000, maxLengthLimit: 5000, rows: 4, rowsMin: 2, rowsMax: 20 },
} as const;

/** Mirrors ALLOWED_TAGS / ALLOWED_ATTR in the frontend's src/utils/sanitize.js. */
export const CUSTOM_FIELD_ALLOWED_TAGS = ['a', 'b', 'i', 'em', 'strong', 'br', 'p', 'ul', 'ol', 'li', 'span'];
export const CUSTOM_FIELD_ALLOWED_ATTR = ['href', 'target', 'rel', 'class'];

/** Label HTML reduced to what the booking frontend would render. */
export function sanitizeCustomFieldHtml(dirty: string | null | undefined): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: CUSTOM_FIELD_ALLOWED_TAGS,
    ALLOWED_ATTR: CUSTOM_FIELD_ALLOWED_ATTR,
  });
}

/**
 * Plain-text version of a label for places that cannot render markup, such
 * as an input placeholder. Same rules as the frontend: <br> becomes a space,
 * remaining whitespace collapses.
 */
export function customFieldHtmlToPlainText(html: string | null | undefined): string {
  if (!html) return '';
  const withSpaces = String(html).replace(/<br\s*\/?>/gi, ' ');
  if (typeof DOMParser === 'undefined') return withSpaces.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  const doc = new DOMParser().parseFromString(sanitizeCustomFieldHtml(withSpaces), 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Stored label -> editor text. The frontend renders the label as HTML, where a
 * raw newline collapses to a space, so line breaks are stored as <br> and
 * shown to the editor as newlines.
 */
export function labelHtmlToEditorText(html: string | null | undefined): string {
  if (!html) return '';
  return String(html).replace(/\r?\n/g, '').replace(/<br\s*\/?>/gi, '\n');
}

/** Editor text -> stored label: every newline becomes <br>. */
export function labelEditorTextToHtml(text: string | null | undefined): string {
  if (!text) return '';
  return String(text).replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
}

/**
 * The hint shown inside an empty field, as the frontend computes it: the
 * configured placeholder, else for a single-line `text` field the label as
 * plain text, else nothing. A `textarea` never falls back to its label.
 */
export function customFieldPlaceholder(field: Pick<CustomFieldConfig, 'type' | 'label' | 'placeholder'>): string {
  if (field.placeholder) return field.placeholder;
  return field.type === 'text' ? customFieldHtmlToPlainText(field.label) : '';
}
